import { useTranslation } from 'next-i18next'
import { forwardRef, useEffect, useRef } from 'react'
import { tsParticles } from 'tsparticles-engine'
import { loadConfettiPreset } from 'tsparticles-preset-confetti'
import { HTTP_CAMPAIGN_TOKEN_HEADER } from '@captaincause/lib/api/headers'
import { getSideConfettisConfig } from '@captaincause/lib/particles/sideConfettis'
import { useNextLocale } from '@captaincause/lib/useNextLocale'
import { DiftWithAmount } from '@captaincause/ui/Dift'
import { Ficello } from '@captaincause/ui/Ficello'
import { Html } from '@captaincause/ui/Html'
import { ProjectCard } from '@captaincause/ui/ProjectCard'
import { tm } from '@captaincause/ui/tm'
import { DifterPageHead } from 'pages/Head'
import { useWidget } from 'utils/widget/Provider'
import { Button } from 'components/Button'
import { CampaignAbout } from 'components/CampaignAbout'
import { CampaignStateBanner } from 'components/CampaignStateBanner'
import { usePanelActions } from 'components/DonatePanel/usePanelActions'
import { GiveProjectsGrid } from 'components/GiveProjectsGrid'
import { LocalizationToggle } from 'components/LocalizationToggle'
import { PreviewDoodle } from 'components/PreviewDoodle'
import { ProjectFiltersBar } from 'components/Project/Filtersbar'
import { ScrollPrompt } from 'components/ScrollPrompt'
import { SmallFooter } from 'components/SmallFooter'
import { Panels } from './Panels'
import { CampaignProgress } from './Progress'
import type {
  Campaign,
  ImageWithMeta,
  Localization,
  ProjectFilters,
  ProjectId,
  Project as ProjectType,
} from '@captaincause/types'
import type { ProjectFiltersBarImperativeRef } from 'components/Project/Filtersbar'

interface PageProps {
  campaign: Campaign
  projects: ProjectType[]
  token: string
  selectedProject: string
  filters?: ProjectFilters | null
  setProjects?: (filteredProjects: ProjectType[]) => void
  onSeeMoreClick?: (id: ProjectId) => void
  shouldHaveFilter?: boolean
  withFooter?: boolean
}

function getLogoShape({ logo_v2: logoV2 }: { logo_v2?: ImageWithMeta }) {
  let isSquare = false
  let isRectangle = false
  let width
  let height

  if (logoV2) {
    width = logoV2.metadata.width || 0
    height = logoV2.metadata.height || 0
    if (height < width * 0.8) {
      isRectangle = true
    } else {
      isSquare = true
    }
  }

  return { isRectangle, isSquare, height, width }
}

const noop = () => {}
function GiveWithRef(
  {
    campaign,
    projects,
    token,
    selectedProject,
    filters = null,
    setProjects = noop,
    shouldHaveFilter = true,
    withFooter = true,
    onSeeMoreClick = noop,
  }: PageProps,
  ref: any,
) {
  const projectIdList = projects.map(({ id }) => id)

  const projectsRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation('projects.page')

  const locale = useNextLocale()
  const mixpanelPayload = {
    campaign_name: campaign.name,
    campaign_preview: campaign.is_preview,
    campaign_test: campaign.is_test,
    campaign_id: campaign.id,
    campaign: campaign.id,
    campaign_amount: campaign.displayed_amount?.amount,
    organization_name: campaign.organization.name,
    project_quantity: projects.length,
    projects_names: projects.map(({ name }) => name),
  }

  const { switchProject, seeMore, close, projectId } = usePanelActions({
    projects,
    selectedProject,
    projectIdList,
    mixpanelPayload,
    onSeeMoreClick,
  })
  const filtersBarRef = useRef<ProjectFiltersBarImperativeRef>(null)

  const { isWidget } = useWidget()

  const logoURL = campaign.logo_v2?.url ?? campaign.logo
  const { isRectangle, isSquare, height, width } = getLogoShape({
    logo_v2: campaign.logo_v2,
  })
  const hasDift = campaign.displayed_amount?.amount

  useEffect(() => {
    async function loadConfetis() {
      if (!campaign.is_done || campaign.is_preview) return

      await loadConfettiPreset(tsParticles)
      tsParticles.load(
        'portals',
        getSideConfettisConfig(
          campaign.primary_color,
          campaign.secondary_color,
        ),
      )
    }

    loadConfetis()
  }, [
    campaign.is_done,
    campaign.is_preview,
    campaign.primary_color,
    campaign.secondary_color,
  ])

  return (
    <>
      <DifterPageHead noIndex />

      <main
        id="main"
        className="relative m-auto flex h-full w-full flex-col overflow-x-hidden"
        ref={ref}
        style={{ background: campaign.primary_color }}
      >
        <CampaignStateBanner campaign={campaign} fixed={isWidget} />
        {campaign.is_preview ? <PreviewDoodle /> : null}
        {!isWidget ? (
          <ScrollPrompt
            campaign={campaign}
            onClick={() => {
              projectsRef.current?.scrollIntoView({ behavior: 'smooth' })
            }}
          />
        ) : null}
        <header className="relative m-auto w-full px-6 py-8 md:p-14">
          <div className="absolute left-1/2 top-0 h-full w-full max-w-[1280px] -translate-x-1/2">
            {hasDift ? (
              <>
                <Ficello
                  variant="virage"
                  className="absolute -top-[39em] left-[600px] hidden rotate-[2deg] scale-[1.25] md:block"
                  style={{ stroke: campaign.secondary_color }}
                />
                <Ficello
                  variant="raye"
                  className="absolute -top-[430px] left-1/2 -translate-x-1/2 rotate-[60deg] scale-[0.58] md:hidden"
                  style={{ stroke: campaign.secondary_color }}
                />
              </>
            ) : (
              <>
                <Ficello
                  variant="camel"
                  className="absolute -right-[42em] -top-[21em] hidden rotate-[22deg] md:block"
                  style={{ stroke: campaign.secondary_color }}
                />
                <Ficello
                  variant="camel"
                  className="absolute -left-[42em] -top-[21em] hidden rotate-[195deg] md:block"
                  style={{ stroke: campaign.secondary_color }}
                />
              </>
            )}
          </div>

          {campaign.available_locales.length > 1 ? (
            <LocalizationToggle
              className="absolute right-4 top-4 rounded-md border-none bg-white bg-opacity-40 p-2"
              localizations={
                campaign.available_locales.map(campaignLocale => ({
                  locale: campaignLocale,
                })) as Localization[]
              }
            />
          ) : null}
          <div
            className={tm(
              'mx-auto flex w-full max-w-screen-2xl flex-col-reverse items-center justify-between gap-14 md:flex-row md:items-start lg:gap-20 xl:gap-44',
              !logoURL && 'md:items-center',
              !hasDift && 'justify-center',
            )}
          >
            <div
              className={tm(
                'flex max-w-lg flex-col items-center justify-between md:max-w-2xl md:items-start',
                !hasDift &&
                  'md:max-w-[500px] md:items-center lg:max-w-2xl xl:max-w-4xl',
                !logoURL && !hasDift && 'md:mt-8 md:max-w-[500px]',
              )}
            >
              {logoURL ? (
                <img
                  src={logoURL}
                  alt={campaign.name}
                  height={height}
                  width={width}
                  style={{ width: 'auto', height }}
                  className={tm(
                    'z-[1] mb-4 max-h-[80px] object-contain md:mb-6',
                    isSquare && 'max-h-28',
                    isRectangle && 'max-w-[400px]',
                  )}
                />
              ) : null}
              <Html
                as="div"
                className={tm(
                  'line z-10 self-center text-center text-sm leading-6 md:text-left md:text-base md:leading-8',
                  !hasDift &&
                    'leading-[30px] md:max-w-md md:text-center md:text-base md:leading-8 lg:max-w-2xl lg:text-sm lg:leading-8 xl:max-w-4xl',
                )}
                style={{ color: campaign.primary_text_color }}
              >
                {campaign.description}
              </Html>
            </div>
            {hasDift ? (
              <div className="relative flex flex-col items-center">
                <DiftWithAmount
                  campaign={campaign}
                  amount={campaign.displayed_amount}
                  className="relative shadow-lg"
                  skipCountUp={
                    !campaign.skip_animation && campaign.is_individual_dift
                  }
                  locale={locale}
                  pointsMode={campaign.points_mode}
                  pointsValueInCents={campaign.points_value_in_cents}
                />
              </div>
            ) : null}
          </div>
        </header>
        <CampaignProgress campaign={campaign} />
        <div className="z-10 rounded-t-[24px] bg-white px-6 md:rounded-t-[32px] lg:px-14">
          {filters && shouldHaveFilter ? (
            <section className="mx-auto w-full max-w-screen-2xl flex-col-reverse items-center justify-between gap-8 rounded-t-[32px] pt-10 md:flex-row md:items-start md:gap-12 lg:gap-20 xl:gap-44">
              <ProjectFiltersBar
                ref={filtersBarRef}
                projects={projects}
                filters={filters}
                onFilter={filteredProjects => setProjects(filteredProjects)}
                withSorting={false}
                searchHTTPHeaders={[[HTTP_CAMPAIGN_TOKEN_HEADER, token]]}
              />
            </section>
          ) : null}
          {projects.length > 0 ? (
            <GiveProjectsGrid size={projects.length} ref={projectsRef}>
              {campaign.is_done
                ? projects
                    .map(project => ({ ...project, is_done: true }))
                    .map((projectItem, index) => (
                      <ProjectCard
                        key={projectItem.id}
                        project={projectItem}
                        locale={locale}
                        Link={Button}
                        promoted={!!projectItem.is_emergency}
                        hasPriorityImage={index < 5}
                        variant="catalog"
                      />
                    ))
                : projects.map((projectItem, index) => (
                    <ProjectCard
                      key={projectItem.id}
                      project={projectItem}
                      locale={locale}
                      donate={seeMore}
                      pointMode={
                        campaign.points_mode && campaign.is_individual_dift
                      }
                      pointsValueInCents={campaign.points_value_in_cents}
                      amount={campaign.displayed_amount}
                      progressBar={campaign.enable_progress_bar}
                      compact={false}
                      hasPriorityImage={index < 5}
                      variant="give"
                    />
                  ))}
            </GiveProjectsGrid>
          ) : (
            <section
              className={tm(
                'my-12 flex flex-col items-center',
                campaign.is_done && 'hidden',
                (!filters || !shouldHaveFilter) && 'hidden',
              )}
            >
              <div className="text-center text-lg">
                {t('search.not-found.title')}
              </div>
              <div className="text-base">{t('search.not-found.retry')} </div>
              <Button
                variant="secondary"
                small
                onClick={filtersBarRef?.current?.clearFilters}
                className="mt-4"
              >
                {t('search.not-found.reset')}
              </Button>
            </section>
          )}
        </div>
        <CampaignAbout
          campaignAbout={campaign.about}
          backgroundColor={campaign.primary_color}
          textColor={campaign.primary_text_color}
          projects={projects}
          innerClassName="max-w-screen-2xl"
        />
        <Panels
          switchProject={switchProject}
          close={close}
          projectId={projectId}
          token={token}
          seeMore={seeMore}
          campaign={campaign}
        />
      </main>
      {withFooter ? <SmallFooter /> : null}
    </>
  )
}

export const Give = forwardRef(GiveWithRef)
